import { useState, useEffect } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/hooks/usePageMeta";
import { ArrowRight } from "lucide-react";

const SYMBOL = `${process.env.PUBLIC_URL}/amanah-symbol.png`;

export default function LoginPage() {
  usePageMeta("Site Settings — Sign in | Amanah Promotions");
  const { user, ready, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ready && user) navigate("/site-settings", { replace: true });
  }, [ready, user, navigate]);

  if (ready && user) return <Navigate to="/site-settings" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) navigate("/site-settings", { replace: true });
    else setError(res.error || "Unable to sign in.");
  };

  return (
    <div className="flex min-h-screen flex-col bg-cream">
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-6 py-16">
        <Link to="/" className="mb-10 flex items-center gap-3" data-testid="login-home-link">
          <img src={SYMBOL} alt="Amanah" className="h-10 w-auto" />
          <span className="font-display text-lg font-medium text-navy">Amanah Promotions</span>
        </Link>

        <div className="border border-line bg-white p-8 sm:p-10">
          <h1 className="font-display text-2xl font-medium text-navy sm:text-3xl">Site Settings</h1>
          <p className="mt-2 text-sm text-slate-body">
            Authorised access only. Please sign in to manage public contact details.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" noValidate>
            <div>
              <label htmlFor="email" className="mb-2 block text-sm font-medium text-navy">
                Email
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 rounded-none border-line bg-white text-navy focus-visible:ring-2 focus-visible:ring-brand-green md:text-base"
                autoComplete="username"
                data-testid="login-email"
                required
              />
            </div>
            <div>
              <label htmlFor="password" className="mb-2 block text-sm font-medium text-navy">
                Password
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 rounded-none border-line bg-white text-navy focus-visible:ring-2 focus-visible:ring-brand-green md:text-base"
                autoComplete="current-password"
                data-testid="login-password"
                required
              />
            </div>

            {error && (
              <p role="alert" className="text-sm font-medium text-red-600" data-testid="login-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="inline-flex w-full items-center justify-center gap-2 bg-brand-green px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-brand-greenDeep disabled:opacity-60"
              data-testid="login-submit"
            >
              {loading ? "Signing in…" : "Sign in"}
              {!loading && <ArrowRight className="h-4 w-4" />}
            </button>
          </form>

          <div className="mt-5 text-center">
            <Link
              to="/forgot-password"
              className="text-sm text-brand-green underline underline-offset-2 hover:text-brand-greenDeep"
              data-testid="forgot-password-link"
            >
              Forgot password?
            </Link>
          </div>
        </div>

        <Link to="/" className="mt-8 text-sm text-slate-muted hover:text-brand-green">
          ← Back to website
        </Link>
      </div>
    </div>
  );
}
